import { forwardRef, useState, type ButtonHTMLAttributes, type CSSProperties } from "react";
import { useEmbedTokens } from "../theme";

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "style"> {
  variant?: "default" | "danger";
}

// Inline text link for ACTIONS (the embed's "Clear filters"/"+ Add new address"/"Remove").
// Padding 0, no height, no border — the shape EmbedButton's fixed geometry can't express.
// TD-329: button-only by design (all call sites are actions, none navigate);
// the spec's optional href/<a> path is deferred as YAGNI until a nav site appears.
export const EmbedLink = forwardRef<HTMLButtonElement, Props>(function EmbedLink(
  { variant = "default", onMouseEnter, onMouseLeave, children, ...rest }: Props,
  ref,
) {
  const t = useEmbedTokens();
  const [hovered, setHovered] = useState(false);
  const color = variant === "danger" ? t.colors.error : t.colors.link;
  const style: CSSProperties = {
    fontFamily: "inherit", fontSize: t.fontSize.sm, fontWeight: t.fontWeight.medium, color,
    // border: 0 (not "none") — React 19 silently drops `border: "none"` from inline
    // styles, which would leave the <button>'s UA `2px outset buttonface` visible.
    background: "transparent", border: 0, padding: 0, margin: 0,
    cursor: "pointer",
    // Task 12 (TD-575 sweep, check c): an action link is a target, not prose.
    // inline-flex + a minimum box gives it WCAG 2.5.8's 24px on a mouse and the
    // touch 44px on a coarse pointer; the label stays start-aligned so the
    // visual text position is unchanged. (ORIGINAL: display:inline, 15px tall.)
    display: "inline-flex", alignItems: "center", justifyContent: "flex-start",
    minHeight: t.pointer === "fine" ? 24 : t.size.touchTarget,
    minWidth: t.pointer === "fine" ? 24 : t.size.touchTarget,
    textDecoration: hovered ? "underline" : "none",
    textAlign: "left",
  };
  return (
    <button ref={ref} type="button" style={style}
      onMouseEnter={(e) => { setHovered(true); onMouseEnter?.(e); }}
      onMouseLeave={(e) => { setHovered(false); onMouseLeave?.(e); }}
      {...rest}>
      {children}
    </button>
  );
});
